import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation, useParams } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { buttonClasses } from "@/components/design/button-classes";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { SelfAssessmentResultDocument } from "@/graphql/generated/operations";
import type { UserRank } from "@/graphql/generated/types";
import { ELEMENT_CODES, elementDisplayName, elementNameId } from "@/lib/elements";
import { elementEvaluation } from "@/lib/element-evaluations";
import { achievementRankText, rankLabel } from "@/lib/grow-rank";

// Grow · self-assessment result screen (own route /grow/:elementId/result).
// Self-assessment result screen: the computed score, the banded evaluation
// text (lib/element-evaluations, sourced from the
// bd-requirements#3 corpus), and the achievement deltas (gained score / new rank)
// against the pre-save snapshot the assessment page hands over via router state.

type ResultNavState = { prevScore?: number | null; prevRank?: UserRank | null } | null;

function Medal({ size = 28 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className="text-ink-accent"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="8" r="6" />
      <path d="M8.5 13.5 7 22l5-3 5 3-1.5-8.5" />
    </svg>
  );
}

export function ElementResultPage() {
  const intl = useIntl();
  const params = useParams<{ elementId: string }>();
  const element = params.elementId ?? "";
  const valid = element in ELEMENT_CODES;

  const { state } = useLocation();
  const nav = state as ResultNavState;

  const { data, loading } = useQuery(SelfAssessmentResultDocument, {
    variables: { element: element as never },
    skip: !valid,
    // After a save the cached values are stale; always pull the recomputed score.
    fetchPolicy: "cache-and-network",
  });

  if (!valid) {
    return (
      <section className="mx-auto max-w-screen-md px-4 py-12" data-graphql="SelfAssessmentResult">
        <h1 id="grow-unknown-element" className="smycles-page-title">
          <FormattedMessage id="grow.elementResult.unknownElement" defaultMessage="Unknown element" />
        </h1>
        <p className="mt-2 text-smycles-body text-ink-muted">{element}</p>
      </section>
    );
  }

  const evalResult = data?.selfAssessmentResult;
  const ranking = data?.growRanking;
  const score = evalResult?.result ?? null;
  const resultText = evalResult?.resultText ?? null;
  const evaluation = elementEvaluation(element, score, intl.locale);
  const completed =
    !!evalResult &&
    evalResult.questionCount > 0 &&
    evalResult.answeredQuestionCount === evalResult.questionCount;

  // growRanking.score is a global completion percentage across all elements,
  // not this element's result - next to "Dein Score" it reads as a second,
  // worse grade. Only the rank delta is shown here; the overall score lives on
  // the Power Map.
  const rankChanged =
    !!ranking && nav?.prevRank != null && ranking.userRank !== nav.prevRank;
  const code = ELEMENT_CODES[element];

  return (
    <section className="mx-auto max-w-screen-md px-4 py-12" data-graphql="SelfAssessmentResult">
      <div className="mb-5">
        <BackButton />
      </div>

      <header className="mb-8 text-center">
        <h1 className="smycles-page-title">{intl.formatMessage({ id: elementNameId(element), defaultMessage: elementDisplayName(element) })}</h1>
        <p id="grow-result-completed" className="mt-2 text-smycles-meta tracking-wide text-ink-muted">
          <FormattedMessage
            id="grow.elementResult.selfAssessmentCompleted"
            defaultMessage="All statements rated"
          />
        </p>
      </header>

      <div id="grow-result" className="box bg-surface p-8 text-center">
        <div id="grow-result-scorelabel" className="smycles-field-label text-ink-muted">
          <FormattedMessage id="grow.elementResult.yourScore" defaultMessage="Your score" />
        </div>
        <div id="grow-result-score" className="mt-1 font-serif text-smycles-display-xl leading-none tabular-nums">
          {score ?? (loading ? "…" : "–")}
        </div>
        {resultText ? (
          <div className="smycles-field-label mt-2 text-ink">{resultText}</div>
        ) : null}
        <div className="mt-4 flex items-center justify-center gap-2 text-smycles-meta text-ink-muted">
          {completed ? (
            <>
              <Medal size={20} />
              <span id="grow-result-fully">
                <FormattedMessage
                  id="grow.elementResult.fullyAnswered"
                  defaultMessage="Fully answered"
                />
              </span>
            </>
          ) : evalResult ? (
            <span>
              <FormattedMessage
                id="grow.elementResult.answeredCount"
                defaultMessage="{answered}/{total} answered"
                values={{
                  answered: evalResult.answeredQuestionCount,
                  total: evalResult.questionCount,
                }}
              />
            </span>
          ) : null}
        </div>
      </div>

      {evaluation ? (
        <div className="box mt-6 bg-surface p-6">
          <div className="smycles-field-label text-ink-muted">
            <FormattedMessage id="grow.elementResult.evaluation" defaultMessage="Evaluation" />
          </div>
          <p className="mt-2 text-smycles-body leading-relaxed text-ink">{evaluation}</p>
        </div>
      ) : null}

      {rankChanged && ranking ? (
        <div className="mt-6">
          <div className="box-flat flex items-center gap-3 bg-surface px-4 py-3">
            <Medal />
            <span className="text-smycles-body text-ink">
              {achievementRankText(rankLabel(ranking.userRank, intl.locale), intl.locale)}
            </span>
          </div>
        </div>
      ) : null}

      {evalResult?.lastEvaluationOn ? (
        <p className="mt-6 text-center text-smycles-label text-ink-muted">
          <FormattedMessage
            id="grow.elementResult.lastEvaluatedOn"
            defaultMessage="Last evaluated: {date}"
            values={{
              date: intl.formatDate(evalResult.lastEvaluationOn, {
                year: "numeric",
                month: "long",
                day: "numeric",
              }),
            }}
          />
        </p>
      ) : null}

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {score != null ? (
          <AskAssistantButton
            variant="primary"
            size="md"
            // Randomize among 10 equivalent phrasings for variety (grow.elementResult.askIntro.0..9).
            prompt={() =>
              intl.formatMessage(
                { id: `grow.elementResult.askIntro.${Math.floor(Math.random() * 10)}` },
                {
                  name: intl.formatMessage({ id: elementNameId(element), defaultMessage: elementDisplayName(element) }),
                  score,
                },
              )
            }
            label={<FormattedMessage id="grow.elementResult.askButton" defaultMessage="Discuss my result" />}
          />
        ) : null}
        <Link to="/grow/map" className={buttonClasses({ variant: "primary" })}>
          <FormattedMessage id="grow.elementResult.toPowerMap" defaultMessage="To Power Map" />
        </Link>
        {code ? (
          <Link to={`/grow/power/${code}`} className={buttonClasses({ variant: "primary" })}>
            <FormattedMessage id="grow.elementResult.toPower" defaultMessage="To Power" />
          </Link>
        ) : null}
        <Link
          id="grow-result-reassess"
          to={`/grow/${element}`}
          className={buttonClasses({ variant: "secondary" })}
        >
          <FormattedMessage id="grow.elementResult.reassess" defaultMessage="Assess again" />
        </Link>
      </div>
    </section>
  );
}
